import React, { useState } from 'react';
import { Lock, Eye, EyeOff, AlertTriangle, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { apiClient } from '../lib/api';
import { toast } from 'sonner';

export const ForcePasswordChangeModal = ({ onSuccess }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const validatePassword = (password) => {
    const requirements = {
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
    };
    return requirements;
  };

  const passwordRequirements = validatePassword(newPassword);
  const allRequirementsMet = Object.values(passwordRequirements).every(Boolean);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    // Validate
    if (!allRequirementsMet) {
      setErrors({ password: 'Password does not meet all requirements' });
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrors({ confirm: 'Passwords do not match' });
      return;
    }

    setLoading(true);
    try {
      const response = await apiClient.forcePasswordChange(newPassword);
      toast.success('Password changed successfully!');
      if (onSuccess) {
        onSuccess(response.data.user);
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to change password';
      toast.error(message);
      setErrors({ submit: message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#0B1D51] to-[#1a3a8f] p-6 text-center">
          <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-xl font-bold text-white">Set Your New Password</h2>
          <p className="text-white/80 text-sm mt-2">
            For security, you need to create a new password before continuing
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Info Banner */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <p className="font-medium">Welcome to Train Driver Psychometrics!</p>
              <p className="mt-1 text-amber-700">Your account was created via Train Driver Foundation. Please set a secure password to continue.</p>
            </div>
          </div>

          {/* New Password */}
          <div>
            <Label htmlFor="new-password">New Password</Label>
            <div className="relative mt-1">
              <Input
                id="new-password"
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter your new password"
                className="pr-10"
                data-testid="new-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Password Requirements */}
          <div className="bg-slate-50 rounded-lg p-3 space-y-2">
            <p className="text-xs font-medium text-slate-600 mb-2">Password requirements:</p>
            {[
              { key: 'minLength', label: 'At least 8 characters' },
              { key: 'hasUppercase', label: 'One uppercase letter' },
              { key: 'hasLowercase', label: 'One lowercase letter' },
              { key: 'hasNumber', label: 'One number' },
            ].map(({ key, label }) => (
              <div key={key} className="flex items-center gap-2 text-xs">
                {passwordRequirements[key] ? (
                  <CheckCircle className="w-3.5 h-3.5 text-green-600" />
                ) : (
                  <div className="w-3.5 h-3.5 rounded-full border-2 border-slate-300" />
                )}
                <span className={passwordRequirements[key] ? 'text-green-700' : 'text-slate-500'}>
                  {label}
                </span>
              </div>
            ))}
          </div>

          {/* Confirm Password */}
          <div>
            <Label htmlFor="confirm-password">Confirm Password</Label>
            <div className="relative mt-1">
              <Input
                id="confirm-password"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your new password"
                className="pr-10"
                data-testid="confirm-password-input"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {errors.confirm && (
              <p className="text-red-500 text-xs mt-1">{errors.confirm}</p>
            )}
          </div>

          {/* Submit Error */}
          {errors.submit && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
              {errors.submit}
            </div>
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={loading || !allRequirementsMet || !confirmPassword}
            className="w-full bg-[#0B1D51] hover:bg-[#0B1D51]/90"
            data-testid="set-password-submit"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                Setting Password...
              </span>
            ) : (
              'Set Password & Continue'
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default ForcePasswordChangeModal;
